/**
 * Body regions: hide the parts of a character's own skin that a garment covers.
 *
 * Overlaying a garment on the bare body makes the skin poke through at joints
 * and gives sleeves a puffy double wall. Instead each body is partitioned by
 * its OWN skin weights (a triangle belongs to the bone that dominates its
 * three vertices) and the triangles a worn garment replaces are left out of
 * the draw. Hands, feet and any region nothing covers stay as skin, so shorts
 * leave the shins, a sleeveless top would leave the arms, and so on.
 *
 * One draw call per character: the body keeps a single mesh whose geometry
 * shares the original vertex buffers and owns only its index list.
 */
import * as THREE from 'three';
import { garmentBaseKey } from './garment-variants';

export type BodyRegion =
  | 'head' | 'neck' | 'torso' | 'hips'
  | 'upper_arm' | 'forearm' | 'hand'
  | 'upper_leg' | 'lower_leg' | 'foot';

/** Regions a garment can hide. head/neck are never covered. */
const BASE_NAMES: Record<string, BodyRegion> = {
  head: 'head',
  neck: 'neck',
  chest: 'torso',
  spine: 'torso',
  shoulder: 'torso',
  hips: 'hips',
  upper_arm: 'upper_arm',
  forearm: 'forearm',
  hand: 'hand',
  upper_leg: 'upper_leg',
  lower_leg: 'lower_leg',
  foot: 'foot',
};

/**
 * Region of a rig bone, side-less. Accepts both the Blender name (`hand.R`)
 * and the name three.js's loader leaves on the bone (`handR`).
 */
export function regionOfBone(boneName: string): BodyRegion | null {
  const plain = boneName.replace(/\./g, '');
  const direct = BASE_NAMES[plain];
  if (direct) return direct;
  if (/[LR]$/.test(plain)) return BASE_NAMES[plain.slice(0, -1)] ?? null;
  return null;
}

const TORSO_SLEEVES: readonly BodyRegion[] = ['torso', 'upper_arm', 'forearm'];
const LONG_COAT: readonly BodyRegion[] = [...TORSO_SLEEVES, 'hips'];
/** A coat whose skirt hangs from the hips past the knee, wrapping the thigh. */
const SKIRTED_COAT: readonly BodyRegion[] = [...LONG_COAT, 'upper_leg'];
const TROUSERS: readonly BodyRegion[] = ['hips', 'upper_leg', 'lower_leg'];
const SHORTS: readonly BodyRegion[] = ['hips', 'upper_leg'];

/**
 * What each garment replaces. Checked against the shipped GLBs' skin weights
 * by `body-regions.test.ts`, so a new garment cannot be added with a guess.
 * Hems must land on a joint: a region is hidden whole or not at all.
 */
export const GARMENT_COVERS: Readonly<Record<string, readonly BodyRegion[]>> = {
  torso_jacket: TORSO_SLEEVES,
  torso_leather: TORSO_SLEEVES,
  torso_lumbershirt: TORSO_SLEEVES,
  torso_puffer: TORSO_SLEEVES,
  torso_raincoat: SKIRTED_COAT,
  'torso_tactical-hoodie': TORSO_SLEEVES,
  torso_trench: SKIRTED_COAT,
  legs_jeans: TROUSERS,
  legs_tactical: TROUSERS,
  legs_cargo: TROUSERS,
  'legs_shorts-cargo': SHORTS,
  'legs_sport-short': SHORTS,
  glove_mitten: ['hand'],
  // The shaft tops out at the knee, so the boot replaces the shin as well.
  feet_boots: ['foot', 'lower_leg'],
  // Armour vests (2026-10-16) are worn OVER a torso garment, so they replace no skin.
  vest_light: [],
  plate_vest: [],
  // ── textured line (2026-10-16, premium_garment_build.py) ──
  torso_operator: TORSO_SLEEVES,
  'torso_carbon-bomber': TORSO_SLEEVES,
  'torso_moto-leather': TORSO_SLEEVES,
  'torso_prism-windbreaker': TORSO_SLEEVES,
  'torso_army-bdu': TORSO_SLEEVES,
  'torso_tactical-combat': TORSO_SLEEVES,
  'torso_survivor-patched': TORSO_SLEEVES,
  'torso_formal-suit': TORSO_SLEEVES,
  'torso_nerd-argyle': TORSO_SLEEVES,
  legs_operator: TROUSERS,
  legs_carbon: TROUSERS,
  legs_moto: TROUSERS,
  'legs_desert-ranger': TROUSERS,
  'legs_army-bdu': TROUSERS,
  'legs_tactical-urban': TROUSERS,
  'legs_survivor-waxed': TROUSERS,
  'legs_formal-suit': TROUSERS,
  'legs_nerd-cords': TROUSERS,
  glove_operator: ['hand'],
  glove_carbon: ['hand'],
  // Golden Set: the frock's tails hang at the back only, so it is a plain top.
  'torso_brocade-frock': TORSO_SLEEVES,
  'legs_gilt-stripe': TROUSERS,
  glove_gilded: ['hand'],
  'feet_gilt-buckle': ['foot', 'lower_leg'],
  glove_moto: ['hand'],
  glove_kevlar: ['hand'],
  glove_army: ['hand'],
  glove_tactical: ['hand'],
  glove_survivor: ['hand'],
  glove_formal: ['hand'],
  glove_nerd: ['hand'],
  feet_operator: ['foot', 'lower_leg'],
  feet_carbon: ['foot', 'lower_leg'],
  feet_moto: ['foot', 'lower_leg'],
  feet_desert: ['foot', 'lower_leg'],
  'feet_army-jungle': ['foot', 'lower_leg'],
  feet_tactical: ['foot', 'lower_leg'],
  feet_survivor: ['foot', 'lower_leg'],
  // Low shoes: no shaft, so the shin stays skin (or trouser).
  'feet_formal-oxford': ['foot'],
  'feet_nerd-sneakers': ['foot'],
  vest_carbon: [],
  vest_kevlar: [],
  'vest_chest-rig': [],
  vest_gilded: [],
  'vest_army-flak': [],
  'vest_tactical-carrier': [],
};

/**
 * Regions a garment hides although none of its triangles are weighted to
 * them: the trench and raincoat skirts hang from the hips to the knee, so
 * they wrap the thigh without driving it. Without this the thigh skin shows
 * through the skirt whenever the legs swing.
 */
export const GARMENT_REACHES: Readonly<Record<string, readonly BodyRegion[]>> = {
  torso_trench: ['upper_leg'],
  torso_raincoat: ['upper_leg'],
};

/**
 * Garments that, when worn, stop every leg garment from being DRAWN. The
 * leg item stays equipped (its stats still apply); it just isn't rendered,
 * because a rigid coat skirt and trousers fight over the same space.
 * Trench and raincoat share the skirt, so they share the rule.
 */
const HIDES_LEG_GARMENTS: ReadonlySet<string> = new Set(['torso_trench', 'torso_raincoat']);

/**
 * Armour vests. They sit OVER a basic torso garment; a long coat goes over
 * them, so under a raincoat or trench the vest stays equipped (its armour still
 * counts) but is not drawn — Janeks, 2026-10-16: armour "shown on basic torso
 * items. Not on raincoat and trench."
 */
export const VEST_KEYS: ReadonlySet<string> = new Set([
  'vest_light', 'plate_vest',
  'vest_carbon', 'vest_kevlar', 'vest_chest-rig', 'vest_gilded', 'vest_army-flak', 'vest_tactical-carrier',
]);

/** Garment keys that should actually be drawn out of what is worn. */
export function drawnGarmentKeys(worn: readonly string[]): string[] {
  const coated = worn.some((key) => HIDES_LEG_GARMENTS.has(garmentBaseKey(key)));
  return coated ? worn.filter((key) => !key.startsWith('legs_') && !VEST_KEYS.has(key)) : [...worn];
}

/** Union of what the given garment keys cover. A colour variant covers what its base does; unknown keys cover nothing. */
export function coveredRegions(keys: Iterable<string>): Set<BodyRegion> {
  const out = new Set<BodyRegion>();
  for (const key of keys) for (const region of GARMENT_COVERS[garmentBaseKey(key)] ?? []) out.add(region);
  return out;
}

/** Triangle -> region by summed skin weight over its three vertices. */
export function partitionTriangles(
  skinned: THREE.SkinnedMesh,
): Map<BodyRegion | 'other', number[]> | null {
  const geometry = skinned.geometry;
  const skinIndex = geometry.getAttribute('skinIndex');
  const skinWeight = geometry.getAttribute('skinWeight');
  const position = geometry.getAttribute('position');
  if (!skinIndex || !skinWeight || !position) return null;

  const bones = skinned.skeleton.bones;
  const regionOfSkinIndex = bones.map((b) => regionOfBone(b.name));
  const index = geometry.getIndex();
  const triCount = (index ? index.count : position.count) / 3;
  const vertexAt = (i: number): number => (index ? index.getX(i) : i);

  const out = new Map<BodyRegion | 'other', number[]>();
  const sums = new Map<BodyRegion | 'other', number>();
  for (let t = 0; t < triCount; t++) {
    sums.clear();
    for (let k = 0; k < 3; k++) {
      const v = vertexAt(t * 3 + k);
      for (let j = 0; j < 4; j++) {
        const w = skinWeight.getComponent(v, j);
        if (w <= 0) continue;
        const region = regionOfSkinIndex[skinIndex.getComponent(v, j)] ?? 'other';
        sums.set(region, (sums.get(region) ?? 0) + w);
      }
    }
    let best: BodyRegion | 'other' = 'other';
    let bestWeight = -1;
    for (const [region, w] of sums) {
      if (w > bestWeight) { best = region; bestWeight = w; }
    }
    let list = out.get(best);
    if (!list) { list = []; out.set(best, list); }
    list.push(vertexAt(t * 3), vertexAt(t * 3 + 1), vertexAt(t * 3 + 2));
  }
  return out;
}

export class BodyRegionMask {
  private readonly original: THREE.BufferGeometry;
  private readonly derived: THREE.BufferGeometry;
  private covered = new Set<BodyRegion>();

  private constructor(
    private readonly body: THREE.SkinnedMesh,
    private readonly triangles: Map<BodyRegion | 'other', number[]>,
  ) {
    this.original = body.geometry;
    this.derived = new THREE.BufferGeometry();
    for (const name of Object.keys(this.original.attributes)) {
      this.derived.setAttribute(name, this.original.getAttribute(name));
    }
    this.derived.boundingSphere = this.original.boundingSphere?.clone() ?? null;
    this.derived.boundingBox = this.original.boundingBox?.clone() ?? null;
    this.rebuild();
    body.geometry = this.derived;
  }

  /**
   * Null when the body can't be partitioned (no skin data, or several
   * materials whose groups an index rebuild would drop) — it then stays whole.
   */
  static create(body: THREE.SkinnedMesh): BodyRegionMask | null {
    if (Array.isArray(body.material) || body.geometry.groups.length > 0) return null;
    const triangles = partitionTriangles(body);
    return triangles ? new BodyRegionMask(body, triangles) : null;
  }

  /** Region of every triangle, for tests and tooling. */
  triangleCounts(): Map<BodyRegion | 'other', number> {
    return new Map([...this.triangles].map(([r, list]) => [r, list.length / 3]));
  }

  setCovered(regions: ReadonlySet<BodyRegion>): void {
    const same = regions.size === this.covered.size && [...regions].every((r) => this.covered.has(r));
    if (same) return;
    this.covered = new Set(regions);
    this.rebuild();
  }

  /** Visible triangle count, for tests. */
  get visibleTriangles(): number {
    return (this.derived.getIndex()?.count ?? 0) / 3;
  }

  restore(): void {
    this.body.geometry = this.original;
  }

  private rebuild(): void {
    const parts: number[] = [];
    for (const [region, list] of this.triangles) {
      if (region !== 'other' && this.covered.has(region)) continue;
      for (const v of list) parts.push(v);
    }
    const max = this.original.getAttribute('position').count;
    const array = max > 65535 ? new Uint32Array(parts) : new Uint16Array(parts);
    this.derived.setIndex(new THREE.BufferAttribute(array, 1));
  }
}
